import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    CreditCard,
    RefreshCcw,
    Calendar,
    AlertTriangle,
    CheckCircle2,
    Settings,
    X
} from 'lucide-react';
import { PaymentsService } from '@/services/payments.service';
import { toast } from 'sonner';
import AutoPaymentSettingsModal from '@/app/(home)/profile/_components/AutoPaymentSettingsModal';

interface Subscription {
    status: number;
    auto_renew_status: number;
    expires_at: string;
    id: string;
    group_product: {
        name: string;
        description: string;
    };
    subscription_price: number;
}

interface OperationsHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: number;
}

const OperationsHistoryModal: React.FC<OperationsHistoryModalProps> = ({
                                                                           isOpen,
                                                                           onClose,
                                                                           userId
                                                                       }) => {
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
    const [selectedSubscription, setSelectedSubscription] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('recurring');
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    useEffect(() => {
    }, [selectedSubscription, isSettingsOpen, subscriptions]);

    useEffect(() => {
        if (isOpen) {
            fetchSubscriptions();
        }
    }, [isOpen]);

    const fetchSubscriptions = async () => {
        try {
            setLoading(true);
            const data = await PaymentsService.getPayments();
            setSubscriptions(data.items);
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (subscription: Subscription) => {
        if (subscription.status === 1) {
            return (
                <Badge className="bg-green-600/20 text-green-400">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Активно
                </Badge>
            );
        }
        return (
            <Badge variant="secondary" className="bg-gray-600/20 text-gray-400">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Неактивно
            </Badge>
        );
    };

    return (
        <>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="bg-gray-900 border-gray-700 max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <div className="flex items-center justify-between">
                            <DialogTitle className="text-white text-xl">
                                📊 История операций и платежей
                            </DialogTitle>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onClose}
                                className="text-gray-400 hover:text-white"
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                    </DialogHeader>

                    <div className="mt-4">
                        {/* Простая навигация вместо Tabs */}
                        <div className="flex space-x-1 bg-gray-800 rounded-lg p-1 mb-6">
                            <button
                                onClick={() => setActiveTab('recurring')}
                                className={`flex-1 py-2 px-3 rounded-md text-sm transition-colors ${
                                    activeTab === 'recurring'
                                        ? 'bg-gray-700 text-white'
                                        : 'text-gray-400 hover:text-gray-300'
                                }`}
                            >
                                Регулярные списания
                            </button>
                            {/*<button*/}
                            {/*    onClick={() => setActiveTab('history')}*/}
                            {/*    className={`flex-1 py-2 px-3 rounded-md text-sm transition-colors ${*/}
                            {/*        activeTab === 'history'*/}
                            {/*            ? 'bg-gray-700 text-white'*/}
                            {/*            : 'text-gray-400 hover:text-gray-300'*/}
                            {/*    }`}*/}
                            {/*>*/}
                            {/*    История платежей*/}
                            {/*</button>*/}
                            {/*<button*/}
                            {/*    onClick={() => setActiveTab('deposits')}*/}
                            {/*    className={`flex-1 py-2 px-3 rounded-md text-sm transition-colors ${*/}
                            {/*        activeTab === 'deposits'*/}
                            {/*            ? 'bg-gray-700 text-white'*/}
                            {/*            : 'text-gray-400 hover:text-gray-300'*/}
                            {/*    }`}*/}
                            {/*>*/}
                            {/*    Пополнения*/}
                            {/*</button>*/}
                        </div>

                        {/* Контент вкладок */}
                        {activeTab === 'recurring' && (
                            <div className="space-y-4">
                                <p className="text-gray-400 text-sm mb-4">
                                    Управление автоматическими списаниями и настройка платежей
                                </p>

                                {loading ? (
                                    <div className="text-center py-8">
                                        <div className="text-gray-400">Загрузка...</div>
                                    </div>
                                ) : subscriptions.length === 0 ? (
                                    <div className="text-center py-8">
                                        <div className="text-gray-400">Регулярные списания не найдены</div>
                                    </div>
                                ) : (
                                    subscriptions.map((subscription) => (
                                        <Card key={subscription.id} className="bg-gray-800/50 border-gray-700">
                                            <CardContent className="p-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div>
                                                        <h3 className="text-white font-medium text-lg">
                                                            {subscription.group_product.name}
                                                        </h3>
                                                        <p className="text-gray-400 text-sm">
                                                            {subscription.group_product.description}
                                                        </p>
                                                    </div>
                                                    {getStatusBadge(subscription)}
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                    {/* Ваши существующие блоки информации остаются без изменений */}
                                                    <div className="flex items-center gap-2 text-gray-300">
                                                        <CreditCard className="w-4 h-4 text-blue-400" />
                                                        <div>
                                                            <p className="text-xs text-gray-400">Сумма списания</p>
                                                            <p className="font-medium">{subscription.subscription_price} ₽</p>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2 text-gray-300">
                                                        <RefreshCcw className="w-4 h-4 text-green-400" />
                                                        <div>
                                                            <p className="text-xs text-gray-400">Периодичность</p>
                                                            <p className="font-medium">Каждый месяц</p>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2 text-gray-300">
                                                        <Calendar className="w-4 h-4 text-purple-400" />
                                                        <div>
                                                            <p className="text-xs text-gray-400">Следующее списание</p>
                                                            <p className="font-medium">
                                                                {new Date(subscription.expires_at).toLocaleDateString()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setSelectedSubscription(subscription.id);
                                                        setIsSettingsOpen(true);
                                                    }}
                                                    className="text-gray-500 hover:text-gray-400 text-sm transition-colors flex items-center gap-1 cursor-pointer"
                                                    type="button"
                                                >
                                                    <Settings className="w-3 h-3" />
                                                    отключить автоплатеж
                                                </button>
                                            </CardContent>
                                        </Card>
                                    ))
                                )}
                            </div>
                        )}

                        {activeTab === 'history' && (
                            <div className="text-center py-8">
                                <div className="text-gray-400">История платежей временно недоступна</div>
                                <p className="text-gray-500 text-sm mt-2">Данные обновляются...</p>
                            </div>
                        )}

                        {activeTab === 'deposits' && (
                            <div className="text-center py-8">
                                <div className="text-gray-400">История пополнений не найдена</div>
                                <Button className="mt-4 bg-green-600 hover:bg-green-700">
                                    Пополнить баланс
                                </Button>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
            {/* Модальное окно настроек автоплатежа */}
            {selectedSubscription && isSettingsOpen && (
                <AutoPaymentSettingsModal
                    subscriptionId={selectedSubscription}
                    isOpen={isSettingsOpen}
                    onClose={() => {
                        setIsSettingsOpen(false);
                        setSelectedSubscription(null);
                    }}
                    subscription={subscriptions.find(s => s.id === selectedSubscription)}
                />
            )}
        </>
    );
};

export default OperationsHistoryModal;
